import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  auditsAPI, type AuditAction, type AuditActionInput, type AuditActionListParams,
} from '../../lib/api';

/** A save from a dialog: true, or the message to show in the dialog. */
export type SaveResult = true | string;

/**
 * What went wrong, in words a user can act on: the server's reason (a field
 * error first, e.g. "Choose an active user of your company."), "no connection"
 * when the request never reached the server, and a server-side hint on a 5xx.
 * Never a bare status code.
 *
 * @param err - the axios error
 * @param fallback - shown when the server gave no reason
 */
export function apiError(err: unknown, fallback: string): string {
  const res = (err as { response?: { status?: number; data?: unknown } })?.response;
  if (!res) return 'No connection to the server. Check your network and try again.';
  const data = res.data && typeof res.data === 'object' && !(res.data instanceof Blob)
    ? res.data as Record<string, unknown> : {};
  const fields = (data['details'] as { fields?: Record<string, string[]> } | undefined)?.fields;
  const [field, messages] = fields ? (Object.entries(fields)[0] ?? []) : [];
  const first = Array.isArray(messages) ? messages[0] : undefined;
  if (field && first) {
    return /^(This field|Ensure this|A valid|Enter a valid)/.test(first) ? `${field.replace(/_/g, ' ')}: ${first}` : first;
  }
  const message = data['message'] ?? data['error'] ?? data['detail'];
  if (typeof message === 'string' && message && message !== 'Validation failed') return message;
  return (res.status ?? 0) >= 500 ? `${fallback} Something went wrong on our side — try again in a moment.` : fallback;
}

function readBlobText(blob: Blob): Promise<string> {
  if (typeof blob.text === 'function') return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

/** `apiError` for blob downloads: the server's JSON reason arrives as a Blob and is read first. */
export async function apiErrorAsync(err: unknown, fallback: string): Promise<string> {
  const res = (err as { response?: { status?: number; data?: unknown } })?.response;
  if (res && res.data instanceof Blob) {
    try {
      const parsed: unknown = JSON.parse(await readBlobText(res.data));
      return apiError({ response: { status: res.status, data: parsed } }, fallback);
    } catch { /* not JSON: fall through */ }
  }
  return apiError(err, fallback);
}

/** The server's error code (e.g. "invalid_state", "audit_lite_off"), if any. */
export function apiErrorCode(err: unknown): string | null {
  const data = (err as { response?: { data?: { code?: unknown } } })?.response?.data;
  return data && typeof data.code === 'string' ? data.code : null;
}

/**
 * Corrective actions with the mutations the Actions views need.
 *
 * @param params - status / owner / supplier / audit / overdue filters
 * @param enabled - false while Audit Lite is off
 * @returns rows, loading / error, `reload`, and raise / update / setStatus / close helpers (toast on result)
 */
export function useAuditActions(params: AuditActionListParams, enabled: boolean = true) {
  const [actions, setActions] = useState<AuditAction[]>([]);
  const [loading, setLoading] = useState<boolean>(enabled);
  const [error, setError] = useState<string | null>(null);
  const key = JSON.stringify(params);

  const reload = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      setActions(await auditsAPI.actions(JSON.parse(key) as AuditActionListParams));
    } catch (err: unknown) {
      console.error('Failed to load audit actions:', err);
      setError(apiError(err, 'Could not load actions.'));
    } finally {
      setLoading(false);
    }
  }, [key, enabled]);

  useEffect(() => {
    void reload();
  }, [reload]);

  /** true on success; otherwise the error message. `inline`: a dialog shows the error, so no toast. */
  const run = async (work: () => Promise<unknown>, success: string, failure: string, inline = false): Promise<SaveResult> => {
    try {
      await work();
      toast.success(success);
      await reload();
      return true;
    } catch (err: unknown) {
      const message = apiError(err, failure);
      if (!inline) toast.error(message);
      return message;
    }
  };

  return {
    actions, loading, error, reload,
    raise: (payload: AuditActionInput) =>
      run(() => auditsAPI.raiseAction(payload), 'Action raised', 'Could not raise the action', true),
    update: (id: number, payload: Partial<AuditActionInput>) =>
      run(() => auditsAPI.updateAction(id, payload), 'Action updated', 'Could not update the action', true),
    setStatus: (id: number, status: 'open' | 'in_progress') =>
      run(() => auditsAPI.setActionStatus(id, status), 'Action updated', 'Could not update the action'),
    close: (id: number, status: 'completed' | 'cancelled', notes: string) =>
      run(() => auditsAPI.closeAction(id, status, notes), 'Action closed', 'Could not close the action', true),
  };
}
